import lodashGet from 'lodash/get';
import {
  SET_PUSHPIN_EXTENSION,
  PUSHPIN_ATTRIBUTES_SET,
  PUSHPIN_BATCH_CREATE,
  PUSHPIN_UPDATE,
  PUSHPIN_BATCH_CLEAR,
  PUSHPIN_SELECT,
  PUSHPINS_DESELECT,
  PUSHPIN_DROP_START,
  PUSHPIN_DROP_END,
  SET_SHEETS_CONFIGURATION,
  TOGGLE_PUSHPIN_VISIBILITY_SUCCESS,
  batchClearPushpinAction,
  batchCreatePushpinAction,
  DELETE_PUSHPIN_SUCCESS,
  removeRfiFromList,
} from '../actions';
import { getActivePushpinAttributes, getCurrentSheets, getAllIssueWrappersSorted, isShowingHiddenRFIs, getCurrentViewType } from '../selectors';
import { isPushpinVisible, isIssueInSheet } from '../../utils/issues-helpers';
import { viewTypes } from '../../constants/view-types';

const isPushpinExist = (issueId, pushpinsControl) => {
  const isGetItemByIdExist = lodashGet(pushpinsControl, 'pushpinExtension.pushPinManager.getItemById');
  return isGetItemByIdExist && pushpinsControl.pushpinExtension.pushPinManager.getItemById(issueId);

};

const shouldCreatePushpin = (issue, pushpinsControl, currentSheets) => {
  return !isPushpinExist(issue.id, pushpinsControl) && isIssueInSheet(issue, currentSheets);
};

/**
 * pushpinsControl instance:
 */

export default (pushpinsControl) => {
  return store => next => action => {

    // eslint-disable-next-line default-case
    switch (action.type) {
      case SET_PUSHPIN_EXTENSION:
        if (!pushpinsControl.pushpinExtension) {
          const pushpinExtension = action.payload;

          if (pushpinExtension && pushpinExtension.updatePattern) {
            pushpinExtension.updatePattern('rfis-void', 80, '#bcc9d1');
            pushpinExtension.updatePattern('rfis-void-selected', 90, '#bcc9d1');
          }
          pushpinsControl.setPushpinExtension(pushpinExtension);
        }
        break;
      case PUSHPIN_BATCH_CREATE:
        action.payload.issues.forEach((issue) => {
          if (shouldCreatePushpin(issue, pushpinsControl,
            action.payload.currentSheet ? [action.payload.currentSheet] : getCurrentSheets(store.getState()))) {
            pushpinsControl.createPushpin(issue);
          }
        });
        break;
      case PUSHPIN_UPDATE: {
        // There might be a chance where we try to update a pushpin which does not exist -
        // When a pushpin gets hidden (and filter for showing hidden pushpins is off), we're still in the RFI
        // details view but we've hidden the pushpin. In case the user will now un-hide the pushpin, it'll trigger a
        // pushpin update before the pushpin is drawn (triggered from RFI_UPDATE_API_FULFILLED handler in jsonapi-middleware).
        if (isPushpinExist(action.payload.id, pushpinsControl)) {
          pushpinsControl.updatePushpin({ ...action.payload, type: 'rfis' });
        }
        break;
      }
      case PUSHPIN_BATCH_CLEAR:
        // This is called if you filter out some issues
        action.payload.forEach((issue) => {
          if (isIssueInSheet(issue, getCurrentSheets(store.getState()))) {
            pushpinsControl.clearSinglePushpin(issue.id);
          }
        });
        break;
      case PUSHPIN_ATTRIBUTES_SET: {
        if (action.payload === null) {
          const activePushpinAttributes = getActivePushpinAttributes(store.getState());
          if (activePushpinAttributes && activePushpinAttributes.isNew) {
            pushpinsControl.clearSinglePushpin(activePushpinAttributes.id);
          }
        }
        break;
      }
      case PUSHPIN_SELECT: {
        const state = store.getState();
        const issue = action.payload;

        // If an issue is selected in the UI and a push pin is about to be shown
        if (isPushpinVisible(issue) || isShowingHiddenRFIs(state)) {
          if (shouldCreatePushpin(issue, pushpinsControl, getCurrentSheets(state))) {
            pushpinsControl.createPushpin(issue);
          }
          pushpinsControl.selectPushpin(issue.id);
        }

        break;
      }
      case PUSHPINS_DESELECT:
        pushpinsControl.deselectPushpins();
        break;
      case PUSHPIN_DROP_START: {
        const issueStatus = action.payload;
        pushpinsControl.startDropPushpin(issueStatus);
        break;
      }
      case PUSHPIN_DROP_END:
        pushpinsControl.endDropPushpin();
        break;
      case SET_SHEETS_CONFIGURATION: {
        // Re-draw pushpins in case of a document sheet change
        const state = store.getState();
        const currentSheets = getCurrentSheets(state);
        const newCurrentSheets = action.payload.currentSheets;

        // This should only happen when we're viewing a single sheet, do nothing if either prev or new sheet list contains more than one sheet
        if (currentSheets.length <= 1 && newCurrentSheets.length <= 1) {
          const currentSheetGuid = lodashGet(currentSheets, '[0].sheetGuid');
          const newSheetGuid = lodashGet(newCurrentSheets, '[0].sheetGuid');

          if (currentSheetGuid && currentSheetGuid !== newSheetGuid) {
            const issues = getAllIssueWrappersSorted(state).map((issueWrapper) => issueWrapper.attributes);
            store.dispatch(batchClearPushpinAction(issues));
            store.dispatch(batchCreatePushpinAction(issues, newCurrentSheets[0]));
          }
        }

        break;
      }
      case TOGGLE_PUSHPIN_VISIBILITY_SUCCESS: {
        const state = store.getState();
        const issue = action.payload;

        // In case we're already displaying hidden RFIs (via filter), there's no need to do anything
        // as the pushpin is already rendered (and should stay like that)
        if (isShowingHiddenRFIs(state)) {
          break;
        }

        if (isPushpinVisible(issue)) {
          // Draw pushpin on document in case we've unhidden a pushpin and it is not already drawn (might be
          // drawn when filter for showing hidden pushpins is toggled on)
          if (shouldCreatePushpin(issue, pushpinsControl, getCurrentSheets(store.getState()))) {
            pushpinsControl.createPushpin(issue);
            pushpinsControl.selectPushpin(issue.id);
          }
        } else {
          // Remove pushpin from document in case we've hidden a pushpin
          pushpinsControl.clearSinglePushpin(issue.id);
        }

        break;
      }
      case DELETE_PUSHPIN_SUCCESS: {
        // pushpin id is equal to rfi id;
        const { pushpinId } = action.payload;
        const state = store.getState();
        const viewType = getCurrentViewType(state);
        if (viewType === viewTypes.DOCUMENT) {
          pushpinsControl.clearSinglePushpin(pushpinId);
          store.dispatch(removeRfiFromList(pushpinId));
        }
        break;
      }
    }

    return next(action);
  };
};
